import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import AdminSidebar from '../components/layout/AdminSidebar';
import {
  Building2,
  Plus,
  Search,
  Filter,
  Phone,
  Mail,
  Globe,
  FileText,
  AlertTriangle,
  CheckCircle,
  Clock,
  Wrench,
  DollarSign,
  ClipboardList,
  Star,
  ChevronRight,
  Loader2,
  X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';

const API = process.env.REACT_APP_BACKEND_URL + '/api/v1';

const SERVICE_TYPES = [
  { value: 'snow_removal', label: 'Snow Removal', icon: '❄️' },
  { value: 'landscaping', label: 'Landscaping', icon: '🌿' },
  { value: 'electrical', label: 'Electrical', icon: '⚡' },
  { value: 'plumbing', label: 'Plumbing', icon: '🔧' },
  { value: 'hvac', label: 'HVAC', icon: '🌡️' },
  { value: 'roofing', label: 'Roofing', icon: '🏠' },
  { value: 'paving', label: 'Paving', icon: '🛣️' },
  { value: 'fencing', label: 'Fencing', icon: '🚧' },
  { value: 'tree_service', label: 'Tree Service', icon: '🌲' },
  { value: 'pest_control', label: 'Pest Control', icon: '🐛' },
  { value: 'cleaning', label: 'Cleaning', icon: '🧹' },
  { value: 'security', label: 'Security', icon: '🔒' },
  { value: 'general_maintenance', label: 'General Maintenance', icon: '🔨' },
  { value: 'other', label: 'Other', icon: '📦' }
];

const STATUS_COLORS = {
  active: 'bg-green-500',
  inactive: 'bg-gray-500',
  pending: 'bg-yellow-500',
  suspended: 'bg-red-500'
};

const PRIORITY_COLORS = {
  low: 'bg-blue-500',
  medium: 'bg-yellow-500',
  high: 'bg-orange-500',
  emergency: 'bg-red-500'
};

const WO_STATUS_COLORS = {
  new: 'bg-blue-500',
  assigned: 'bg-purple-500',
  in_progress: 'bg-yellow-500',
  completed: 'bg-green-500',
  approved: 'bg-green-700',
  paid: 'bg-emerald-600',
  cancelled: 'bg-gray-500'
};

const VendorManagementPage = () => {
  const [activeTab, setActiveTab] = useState('vendors');
  const [vendors, setVendors] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [complianceAlerts, setComplianceAlerts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showVendorDialog, setShowVendorDialog] = useState(false);
  const [showWorkOrderDialog, setShowWorkOrderDialog] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  
  const [newVendor, setNewVendor] = useState({
    company_name: '',
    primary_contact: '',
    phone: '',
    email: '',
    website: '',
    services: [],
    notes: ''
  });
  
  const [newWorkOrder, setNewWorkOrder] = useState({
    title: '',
    description: '',
    category: 'general_maintenance',
    priority: 'medium',
    vendor_id: '',
    estimated_cost: 0
  });

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [vendorsRes, workOrdersRes, invoicesRes, dashboardRes, alertsRes] = await Promise.all([
        axios.get(`${API}/vendors`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/vendors/work-orders/list`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/vendors/invoices/list`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/vendors/dashboard`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/vendors/compliance-alerts`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setVendors(vendorsRes.data || []);
      setWorkOrders(workOrdersRes.data || []);
      setInvoices(invoicesRes.data || []);
      setDashboard(dashboardRes.data);
      setComplianceAlerts(alertsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load vendor data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateVendor = async () => {
    if (!newVendor.company_name || !newVendor.email) {
      toast.error('Company name and email are required');
      return;
    }

    try {
      const response = await axios.post(`${API}/vendors`, newVendor, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVendors([response.data.vendor, ...vendors]);
      setShowVendorDialog(false);
      setNewVendor({ company_name: '', primary_contact: '', phone: '', email: '', website: '', services: [], notes: '' });
      toast.success('Vendor created successfully');
    } catch (error) {
      toast.error('Failed to create vendor');
    }
  };

  const handleCreateWorkOrder = async () => {
    if (!newWorkOrder.title || !newWorkOrder.description) {
      toast.error('Title and description are required');
      return;
    }

    try {
      const response = await axios.post(`${API}/vendors/work-orders`, newWorkOrder, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWorkOrders([response.data.work_order, ...workOrders]);
      setShowWorkOrderDialog(false);
      setNewWorkOrder({ title: '', description: '', category: 'general_maintenance', priority: 'medium', vendor_id: '', estimated_cost: 0 });
      toast.success('Work order created');
    } catch (error) {
      toast.error('Failed to create work order');
    }
  };

  const handleUpdateWorkOrderStatus = async (workOrderId, status) => {
    try {
      await axios.put(`${API}/vendors/work-orders/${workOrderId}`, { status }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
      toast.success(`Work order ${status}`);
    } catch (error) {
      toast.error('Failed to update work order');
    }
  };

  const toggleService = (service) => {
    const current = newVendor.services || [];
    if (current.includes(service)) {
      setNewVendor({ ...newVendor, services: current.filter(s => s !== service) });
    } else {
      setNewVendor({ ...newVendor, services: [...current, service] });
    }
  };

  const filteredVendors = vendors.filter(v =>
    v.company_name?.toLowerCase().includes(search.toLowerCase()) ||
    v.primary_contact?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-stone-50">
        <Loader2 className="w-8 h-8 animate-spin text-amber-700" />
      </div>
    );
  }

  const pageContent = (
    <div className="p-6 lg:p-8">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white px-6 py-6 rounded-xl mb-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                <Building2 className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">Vendor Management</h1>
                <p className="text-slate-300">Contractors, Work Orders & Invoices</p>
              </div>
            </div>
            <div className="flex gap-3">
              <Button onClick={() => setShowWorkOrderDialog(true)} variant="outline" className="border-white/30 text-white hover:bg-white/10">
                <ClipboardList className="w-4 h-4 mr-2" />
                New Work Order
              </Button>
              <Button onClick={() => setShowVendorDialog(true)} className="bg-white text-slate-800 hover:bg-slate-100">
                <Plus className="w-4 h-4 mr-2" />
                Add Vendor
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto">
        {/* Dashboard Stats */}
        {dashboard && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <Card>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="p-3 rounded-lg bg-blue-100">
                  <Building2 className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{dashboard.active_vendors}</p>
                  <p className="text-sm text-gray-600">Active Vendors</p>
                </div>
              </CardContent>
            </Card>
            
            <Card className={dashboard.open_work_orders > 0 ? 'border-yellow-500' : ''}>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="p-3 rounded-lg bg-yellow-100">
                  <ClipboardList className="w-6 h-6 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{dashboard.open_work_orders}</p>
                  <p className="text-sm text-gray-600">Open Work Orders</p>
                </div>
              </CardContent>
            </Card>
            
            <Card className={complianceAlerts?.expired?.length > 0 ? 'border-red-500 bg-red-50' : ''}>
              <CardContent className="p-4 flex items-center gap-4">
                <div className={`p-3 rounded-lg ${complianceAlerts?.expired?.length > 0 ? 'bg-red-500 text-white' : 'bg-green-100'}`}>
                  {complianceAlerts?.expired?.length > 0 ? (
                    <AlertTriangle className="w-6 h-6" />
                  ) : (
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  )}
                </div>
                <div>
                  <p className="text-2xl font-bold">{complianceAlerts?.expired?.length || 0}</p>
                  <p className="text-sm text-gray-600">Compliance Issues</p>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="p-3 rounded-lg bg-purple-100">
                  <DollarSign className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{invoices.filter(i => i.status === 'pending').length}</p>
                  <p className="text-sm text-gray-600">Pending Invoices</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Compliance Alerts */}
        {complianceAlerts && (complianceAlerts.expired?.length > 0 || complianceAlerts.expiring_soon?.length > 0) && (
          <Card className="mb-6 border-amber-500 bg-amber-50">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                Compliance Alerts
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {complianceAlerts.expired?.map((vendor, i) => (
                  <div key={i} className="flex items-center justify-between p-2 bg-red-100 rounded">
                    <span className="font-medium text-red-800">{vendor.company_name}</span>
                    <Badge className="bg-red-500">Expired Documents</Badge>
                  </div>
                ))}
                {complianceAlerts.expiring_soon?.map((vendor, i) => (
                  <div key={i} className="flex items-center justify-between p-2 bg-yellow-100 rounded">
                    <span className="font-medium text-yellow-800">{vendor.company_name}</span>
                    <Badge className="bg-yellow-500">Expiring Soon</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="vendors">Vendors ({vendors.length})</TabsTrigger>
            <TabsTrigger value="work-orders">Work Orders ({workOrders.length})</TabsTrigger>
            <TabsTrigger value="invoices">Invoices ({invoices.length})</TabsTrigger>
          </TabsList>

          {/* Vendors Tab */}
          <TabsContent value="vendors">
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search vendors..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div className="grid gap-4">
              {filteredVendors.length === 0 ? (
                <Card className="p-12 text-center">
                  <Building2 className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                  <h3 className="text-xl font-semibold mb-2">No vendors yet</h3>
                  <p className="text-gray-600 mb-4">Add your first vendor to get started</p>
                  <Button onClick={() => setShowVendorDialog(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Vendor
                  </Button>
                </Card>
              ) : (
                filteredVendors.map((vendor) => (
                  <Card key={vendor.vendor_id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                            <Building2 className="w-6 h-6 text-slate-600" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold text-lg">{vendor.company_name}</h3>
                              <Badge className={STATUS_COLORS[vendor.status]}>{vendor.status}</Badge>
                            </div>
                            <p className="text-sm text-gray-600">{vendor.primary_contact}</p>
                            <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                              {vendor.phone && (
                                <span className="flex items-center gap-1">
                                  <Phone className="w-3 h-3" />
                                  {vendor.phone}
                                </span>
                              )}
                              {vendor.email && (
                                <span className="flex items-center gap-1">
                                  <Mail className="w-3 h-3" />
                                  {vendor.email}
                                </span>
                              )}
                            </div>
                            <div className="flex flex-wrap gap-1 mt-2">
                              {vendor.services?.map((service, i) => {
                                const svc = SERVICE_TYPES.find(s => s.value === service);
                                return (
                                  <Badge key={i} variant="outline" className="text-xs">
                                    {svc?.icon} {svc?.label || service}
                                  </Badge>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-500">
                            {vendor.completed_work_orders || 0}/{vendor.total_work_orders || 0} completed
                          </p>
                          {vendor.rating && (
                            <div className="flex items-center gap-1 justify-end mt-1">
                              <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                              <span className="font-medium">{vendor.rating}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Work Orders Tab */}
          <TabsContent value="work-orders">
            <div className="grid gap-4">
              {workOrders.length === 0 ? (
                <Card className="p-12 text-center">
                  <ClipboardList className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                  <h3 className="text-xl font-semibold mb-2">No work orders</h3>
                  <p className="text-gray-600 mb-4">Create a work order to track maintenance tasks</p>
                  <Button onClick={() => setShowWorkOrderDialog(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Work Order
                  </Button>
                </Card>
              ) : (
                workOrders.map((wo) => (
                  <Card key={wo.work_order_id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Badge className={PRIORITY_COLORS[wo.priority]}>{wo.priority}</Badge>
                            <Badge className={WO_STATUS_COLORS[wo.status]}>{wo.status.replace('_', ' ')}</Badge>
                          </div>
                          <h3 className="font-semibold">{wo.title}</h3>
                          <p className="text-sm text-gray-600 line-clamp-2">{wo.description}</p>
                          <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                            {wo.vendor_name && (
                              <span className="flex items-center gap-1">
                                <Building2 className="w-3 h-3" />
                                {wo.vendor_name}
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {new Date(wo.created_at).toLocaleDateString()}
                            </span>
                            {wo.estimated_cost > 0 && (
                              <span className="flex items-center gap-1">
                                <DollarSign className="w-3 h-3" />
                                ${wo.estimated_cost.toLocaleString()}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {wo.status === 'in_progress' && (
                            <Button size="sm" variant="outline" onClick={() => handleUpdateWorkOrderStatus(wo.work_order_id, 'completed')}>
                              Mark Complete
                            </Button>
                          )}
                          {wo.status === 'completed' && (
                            <Button size="sm" onClick={() => handleUpdateWorkOrderStatus(wo.work_order_id, 'approved')}>
                              Approve
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Invoices Tab */}
          <TabsContent value="invoices">
            <div className="grid gap-4">
              {invoices.length === 0 ? (
                <Card className="p-12 text-center">
                  <FileText className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                  <h3 className="text-xl font-semibold mb-2">No invoices</h3>
                  <p className="text-gray-600">Vendor invoices will appear here</p>
                </Card>
              ) : (
                invoices.map((invoice) => (
                  <Card key={invoice.invoice_id}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold">#{invoice.invoice_number}</h3>
                            <Badge variant={invoice.status === 'paid' ? 'default' : 'outline'}>
                              {invoice.status}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-600">{invoice.vendor_name}</p>
                          <p className="text-sm text-gray-500">{invoice.description}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold">${invoice.amount.toLocaleString()}</p>
                          <p className="text-sm text-gray-500">
                            {new Date(invoice.invoice_date).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Create Vendor Dialog */}
      <Dialog open={showVendorDialog} onOpenChange={setShowVendorDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Vendor</DialogTitle>
            <DialogDescription>Add a contractor or service provider to your vendor database.</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Company Name *</label>
                <Input
                  value={newVendor.company_name}
                  onChange={(e) => setNewVendor({ ...newVendor, company_name: e.target.value })}
                  placeholder="ABC Landscaping"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Primary Contact</label>
                <Input
                  value={newVendor.primary_contact}
                  onChange={(e) => setNewVendor({ ...newVendor, primary_contact: e.target.value })}
                  placeholder="John Smith"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Phone</label>
                <Input
                  value={newVendor.phone}
                  onChange={(e) => setNewVendor({ ...newVendor, phone: e.target.value })}
                  placeholder="303-555-0123"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Email *</label>
                <Input
                  type="email"
                  value={newVendor.email}
                  onChange={(e) => setNewVendor({ ...newVendor, email: e.target.value })}
                  placeholder="contact@vendor.com"
                />
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium mb-1 block">Website</label>
              <Input
                value={newVendor.website}
                onChange={(e) => setNewVendor({ ...newVendor, website: e.target.value })}
                placeholder="https://www.vendor.com"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium mb-2 block">Services Provided</label>
              <div className="flex flex-wrap gap-2">
                {SERVICE_TYPES.map((service) => (
                  <Button
                    key={service.value}
                    type="button"
                    size="sm"
                    variant={newVendor.services?.includes(service.value) ? 'default' : 'outline'}
                    onClick={() => toggleService(service.value)}
                  >
                    {service.icon} {service.label}
                  </Button>
                ))}
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium mb-1 block">Notes</label>
              <Textarea
                value={newVendor.notes}
                onChange={(e) => setNewVendor({ ...newVendor, notes: e.target.value })}
                placeholder="Additional notes about this vendor..."
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVendorDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateVendor}>Add Vendor</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Work Order Dialog */}
      <Dialog open={showWorkOrderDialog} onOpenChange={setShowWorkOrderDialog}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Create Work Order</DialogTitle>
            <DialogDescription>Create a new maintenance or service request.</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Title *</label>
              <Input
                value={newWorkOrder.title}
                onChange={(e) => setNewWorkOrder({ ...newWorkOrder, title: e.target.value })}
                placeholder="Snow removal - Main entrance"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium mb-1 block">Description *</label>
              <Textarea
                value={newWorkOrder.description}
                onChange={(e) => setNewWorkOrder({ ...newWorkOrder, description: e.target.value })}
                placeholder="Detailed description of the work needed..."
                rows={3}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Category</label>
                <Select
                  value={newWorkOrder.category}
                  onValueChange={(value) => setNewWorkOrder({ ...newWorkOrder, category: value })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="snow_removal">Snow Removal</SelectItem>
                    <SelectItem value="landscaping">Landscaping</SelectItem>
                    <SelectItem value="repairs">Repairs</SelectItem>
                    <SelectItem value="electrical">Electrical</SelectItem>
                    <SelectItem value="plumbing">Plumbing</SelectItem>
                    <SelectItem value="road_maintenance">Road Maintenance</SelectItem>
                    <SelectItem value="common_area">Common Area</SelectItem>
                    <SelectItem value="safety">Safety</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-sm font-medium mb-1 block">Priority</label>
                <Select
                  value={newWorkOrder.priority}
                  onValueChange={(value) => setNewWorkOrder({ ...newWorkOrder, priority: value })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">🔵 Low</SelectItem>
                    <SelectItem value="medium">🟡 Medium</SelectItem>
                    <SelectItem value="high">🟠 High</SelectItem>
                    <SelectItem value="emergency">🔴 Emergency</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Assign to Vendor</label>
                <Select
                  value={newWorkOrder.vendor_id}
                  onValueChange={(value) => setNewWorkOrder({ ...newWorkOrder, vendor_id: value })}
                >
                  <SelectTrigger><SelectValue placeholder="Select vendor (optional)" /></SelectTrigger>
                  <SelectContent>
                    {vendors.filter(v => v.status === 'active').map((vendor) => (
                      <SelectItem key={vendor.vendor_id} value={vendor.vendor_id}>
                        {vendor.company_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-sm font-medium mb-1 block">Estimated Cost</label>
                <Input
                  type="number"
                  value={newWorkOrder.estimated_cost}
                  onChange={(e) => setNewWorkOrder({ ...newWorkOrder, estimated_cost: parseFloat(e.target.value) || 0 })}
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowWorkOrderDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateWorkOrder}>Create Work Order</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
  );

  return <AdminSidebar>{pageContent}</AdminSidebar>;
};

export default VendorManagementPage;
